"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Metadata = exports.EntitySet = exports.NavigationBinding = exports.EntityType = exports.Property = exports.Type = exports.ODataFunction = exports.ODataAction = void 0;
const lodash_1 = require("lodash");
const Metadata_utils_1 = require("./Metadata.utils");
class Type {
    constructor(name, namespace, isCollection) {
        this.name = name;
        this.namespace = namespace;
        this._isCollection = isCollection;
    }
    getName() {
        return this.name;
    }
    getNamespace() {
        return this.namespace;
    }
    getFullName() {
        return this.namespace + "." + this.name;
    }
    isCollection() {
        return this._isCollection;
    }
    toString() {
        const str = this.namespace + "." + this.name;
        return this._isCollection ? `Collection(${str})` : str;
    }
    getExplicitName() {
        return `${this.getName()}${this.isCollection() ? "s" : ""}`;
    }
}
exports.Type = Type;
class Property {
    constructor(name, type, parameters, entityType) {
        this.type = type;
        this.name = name;
        this.entityType = entityType;
        this._isNavigation = !!parameters.navigation;
        this._isKey = !!parameters.key;
        this._isNullable = !!parameters.nullable;
        this._maxLength = (parameters.maxLength && parseInt(parameters.maxLength)) || null;
        this._precision = (parameters.precision && parseInt(parameters.precision)) || null;
        this._scale = (parameters.scale && parseInt(parameters.scale)) || null;
        this._defaultValue = parameters.defaultValue || null;
        this._isReadOnly = !!parameters.readOnly;
        this._isSubordinate = !!parameters.subordinate;
        this._isIncludable = !!parameters.includable;
        this._isDependent = !!parameters.dependent;
        this._isIndependent = !!parameters.independent;
        this._isImmutable = !!parameters.immutable;
        this._isInternal = !!parameters.internal;
        this._displayFormat = parameters.displayFormat;
        this._currency = parameters.currency;
        this._referentialConstraint = parameters.referentialConstraint;
        this._codeFor = parameters.codeFor;
    }
    getType() {
        return this.type;
    }
    getName() {
        return this.name;
    }
    getEntityType() {
        return this.entityType;
    }
    isKey() {
        return this._isKey;
    }
    isNavigation() {
        return this._isNavigation;
    }
    isNullable() {
        return this._isNullable;
    }
    getMaxLength() {
        return this._maxLength;
    }
    getPrecision() {
        return this._precision;
    }
    getScale() {
        return this._scale;
    }
    getDefaultValue() {
        return this._defaultValue;
    }
    isReadOnly() {
        return this._isReadOnly;
    }
    /** Specifies whether the property has its own entity set (false) or has to be accessed via parent entity set (true) */
    isSubordinate() {
        return this._isSubordinate;
    }
    /** This navigation entity can only be included together with parent in the first POST request.
     * Consequent patches has to be done on the EntitySet of the navigation entity itself.*/
    isIncludable() {
        return this._isIncludable;
    }
    /** This navigation can only be updated via the parent entity,
     * even though there can exist separate (readonly) EntitySet just for this entity.
     * */
    isDependent() {
        return this._isDependent;
    }
    /** This property can be used on navigation collections of independent entities (with own entity set).
     * It specifies that when we create request to remove item from the collection,
     * we shouldn't call DELETE on the item itself, but rather just delete the reference by using $ref.
     * Example: PostingRule has collection of CompanyBankAccounts. CompanyBankAccounts has its own entity set.
     * When user remove item from the collection, we don't want to remove the CompanyBankAccount itself,
     * we just want to remove the reference to it from PostingRule.
     * */
    isIndependent() {
        return this._isIndependent;
    }
    isImmutable() {
        return this._isImmutable;
    }
    isInternal() {
        return this._isInternal;
    }
    getDisplayFormat() {
        return this._displayFormat;
    }
    getCurrency() {
        return this._currency;
    }
    getReferentialConstraint() {
        return this._referentialConstraint;
    }
    getCodeFor() {
        return this._codeFor;
    }
}
exports.Property = Property;
class EntityType {
    constructor(name, namespace, properties) {
        this.name = name;
        this.namespace = namespace;
        this.properties = properties;
    }
    getName() {
        return this.name;
    }
    getNamespace() {
        return this.namespace;
    }
    getProperties() {
        var _a;
        if (!this.allProperties) {
            this.allProperties = { ...(_a = this.baseType) === null || _a === void 0 ? void 0 : _a.getProperties(), ...this.properties };
        }
        return this.allProperties;
    }
    getProperty(name) {
        return this.getProperties()[name];
    }
    setBaseType(baseType) {
        this.baseType = baseType;
    }
    getBaseType() {
        return this.baseType;
    }
    hasParentWithBaseType(type) {
        let baseType = this.getBaseType();
        while ((baseType === null || baseType === void 0 ? void 0 : baseType.getBaseType()) && (baseType === null || baseType === void 0 ? void 0 : baseType.getName()) !== type) {
            baseType = baseType.getBaseType();
        }
        return (baseType === null || baseType === void 0 ? void 0 : baseType.getName()) === type;
    }
    getFullName() {
        return this.namespace ? `${this.namespace}.${this.name}` : this.name;
    }
    toString() {
        return this.name;
    }
    getKeys() {
        return Object.values(this.getProperties()).filter(p => p.isKey());
    }
    getNavigationProperties() {
        return Object.values(this.getProperties()).filter(p => p.isNavigation());
    }
    /**
     * Returns properties meant for UI
     */
    getUiProperties() {
        return Object.values(this.getProperties()).filter(p => !p.isInternal());
    }
}
exports.EntityType = EntityType;
class NavigationBinding {
    constructor(path, target) {
        this.path = path;
        this.target = target;
    }
    getPath() {
        return this.path;
    }
    getTarget() {
        return this.target;
    }
}
exports.NavigationBinding = NavigationBinding;
class EntitySet {
    constructor(name, type, navigationBindings) {
        this._name = name;
        this._type = type;
        this.navigationBindings = navigationBindings;
    }
    getName() {
        return this._name;
    }
    getType() {
        return this._type;
    }
    toString() {
        return this._name;
    }
}
exports.EntitySet = EntitySet;
class ODataAction {
    constructor(name, parameters, returnType, isBound) {
        this._name = name;
        this._returnType = returnType;
        this._isBound = isBound;
        this._parameters = parameters.filter(parameter => parameter.name !== "bindingParameter");
        this._bindingParameter = parameters.find(parameter => parameter.name === "bindingParameter");
    }
    getName() {
        return this._name;
    }
    getFullName() {
        const bindingParameter = this.getBindingParameter();
        return bindingParameter
            ? `${bindingParameter.type.getExplicitName()}${this.getName()}`
            : this.getName();
    }
    getBindingParameter() {
        return this._bindingParameter;
    }
    getParameters() {
        return this._parameters;
    }
    getReturnType() {
        return this._returnType;
    }
}
exports.ODataAction = ODataAction;
class ODataFunction {
    constructor(name, parameters, returnType, isBound) {
        this._name = name;
        this._returnType = returnType;
        this._isBound = isBound;
        this._parameters = parameters.filter(parameter => parameter.name !== "bindingParameter");
        this._bindingParameter = parameters.find(parameter => parameter.name === "bindingParameter");
    }
    getName() {
        return this._name;
    }
    getFullName() {
        const bindingParameter = this.getBindingParameter();
        return bindingParameter
            ? `${bindingParameter.type.getExplicitName()}${this.getName()}`
            : this.getName();
    }
    getBindingParameter() {
        return this._bindingParameter;
    }
    getParameters() {
        return this._parameters;
    }
    getReturnType() {
        return this._returnType;
    }
}
exports.ODataFunction = ODataFunction;
class Metadata {
    constructor(url, entities, entitySets, actions, functions = {}) {
        this.getEntitySet = (path) => {
            const { entitySet, navigation } = this.getLastValidEntitySet(path);
            if (navigation) {
                throw new Error(`Metadata: Wrong entity path: ${path}`);
            }
            return entitySet;
        };
        this.getLastValidEntitySet = (path) => {
            const pathParts = path.split("/");
            const firstPart = (0, Metadata_utils_1.parseEntityKey)(pathParts[0]);
            const length = pathParts.length;
            let currentEntitySet = this.entitySets[firstPart.path];
            let lastEntitySetKey = firstPart.key;
            let lastEntitySet = currentEntitySet;
            let lastEntitySetIndex = 0;
            let currentEntityType = this.entities[currentEntitySet ? currentEntitySet.getType().getFullName() : firstPart.path];
            for (let i = 1; i < length; i++) {
                if (!currentEntityType) {
                    throw new Error(`Metadata: Wrong entity path: ${path}`);
                }
                const part = (0, Metadata_utils_1.parseEntityKey)(pathParts[i]);
                currentEntityType = this.entities[currentEntityType.getProperty(part.path).getType().getFullName()];
                if (currentEntitySet) {
                    const navigation = currentEntitySet.navigationBindings[part.path];
                    if (!navigation) {
                        currentEntitySet = this.getEntitySetForEntityType(currentEntityType);
                    }
                    else {
                        currentEntitySet = this.entitySets[navigation.getTarget()];
                    }
                }
                else if (currentEntityType) {
                    currentEntitySet = this.getEntitySetForEntityType(currentEntityType);
                }
                if (currentEntitySet) {
                    lastEntitySet = currentEntitySet;
                    lastEntitySetIndex = i;
                    lastEntitySetKey = part.key;
                }
            }
            return {
                entitySet: lastEntitySet,
                entityKey: lastEntitySetKey,
                navigation: pathParts.slice(lastEntitySetIndex + (lastEntitySet ? 1 : 0)).join("/")
            };
        };
        /** Tries to find entity set for given entity type. If more than one exists, throws an error */
        this.getEntitySetForEntityType = (0, lodash_1.memoize)((entityType) => {
            if (!entityType) {
                return null;
            }
            const entityName = entityType.getFullName();
            const entitySets = Object.values(this.entitySets).filter((entitySet) => {
                return entitySet.getType().getFullName() === entityName;
            });
            if (entitySets.length > 1) {
                throw new Error(`Metadata: Multiple entity sets found for: ${entityName}`);
            }
            return entitySets[0];
        });
        this.url = url;
        this.entities = entities;
        this.entitySets = entitySets;
        this.actions = actions;
        this.functions = functions;
    }
    getUrl() {
        return this.url;
    }
    /**
     * Returns an entity type for provided navigation path. The path happens upon EntitySet and it's navigationBindings and is translated to target type.
     *
     * @param {string} path Path segments
     */
    getTypeForPath(path) {
        if (this.entities[path]) {
            return this.entities[path];
        }
        let entityType, navigatedEntityType;
        const { entitySet, navigation } = this.getLastValidEntitySet(path);
        if (entitySet) {
            entityType = this.entities[entitySet.getType().getFullName()];
        }
        // collections doesn't have to have its own entity set, but their entity type should still be returned
        if (navigation) {
            const navigationParts = navigation.split("/").map(nav => (0, Metadata_utils_1.parseEntityKey)(nav).path);
            if (!entityType) {
                entityType = this.entities[navigationParts.shift()];
            }
            for (const navigPart of navigationParts) {
                if (!entityType.getProperty(navigPart)) {
                    throw new Error(`Metadata: Wrong entity path: ${path}`);
                }
                navigatedEntityType = this.entities[entityType.getProperty(navigPart).getType().getFullName()];
                if (navigatedEntityType) {
                    entityType = navigatedEntityType;
                }
            }
        }
        return entityType;
    }
}
exports.Metadata = Metadata;
